import {
  ModalForm,
  ProFormDateTimePicker,
  ProFormSelect,
  ProFormText,
} from '@ant-design/pro-components';
import { App, Button, Form } from 'antd';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { createCollection, loadFeishuMetadata, updateCollection } from '../services/collect';
import {
  createLatestRequestGuard,
  getFeishuCursorFieldOptions,
  getFeishuSourceSelection,
} from '../utils/feishu-collection';
import {
  buildFeishuStoreCollectionValues,
  getFeishuStoreCollectionInitialValues,
  getFeishuStoreCollectionResetValues,
  STORE_FIELD_MAPPINGS,
} from '../utils/feishu-store-collection';

const AgentFeishuStoreCollectionForm = ({
  collectionType,
  connections,
  onSaved,
  organizationId,
  record,
  trigger,
}) => {
  const [form] = Form.useForm();
  const metadataRequestGuardRef = useRef(createLatestRequestGuard());
  const [metadata, setMetadata] = useState({ fields: [], tables: [], views: [] });
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [metadataLoaded, setMetadataLoaded] = useState(false);
  const { message } = App.useApp();
  const editing = Boolean(record);

  const resetSourceMetadata = () => {
    metadataRequestGuardRef.current.invalidate();
    setMetadata({ fields: [], tables: [], views: [] });
    setLoadingMetadata(false);
    setMetadataLoaded(false);
    form.setFieldsValue(
      getFeishuStoreCollectionResetValues({
        appToken: undefined,
        tableId: undefined,
        viewId: undefined,
      }),
    );
  };

  const loadMetadata = async (options = {}) => {
    const connectionId = options.connectionId || form.getFieldValue('connectionId');
    const sourceReference = (
      options.sourceReference || form.getFieldValue('sourceReference')
    )?.trim();
    if (!connectionId || !sourceReference) {
      throw new Error('请先选择连接并输入多维表格链接');
    }
    const sourceSelection = options.applySourceSelection
      ? getFeishuSourceSelection(sourceReference)
      : {};
    const tableId = options.tableId || sourceSelection.tableId;
    const viewId = options.viewId || sourceSelection.viewId;
    const requestId = metadataRequestGuardRef.current.start();
    setLoadingMetadata(true);
    setMetadataLoaded(false);
    try {
      const result = await loadFeishuMetadata({
        connectionId,
        organizationId,
        ...(sourceReference.startsWith('http')
          ? { sourceUrl: sourceReference }
          : { appToken: sourceReference }),
        ...(tableId ? { tableId } : {}),
      });
      if (!metadataRequestGuardRef.current.isLatest(requestId)) {
        return undefined;
      }
      setMetadata(result);
      setMetadataLoaded(true);
      form.setFieldsValue({
        sourceConfig: {
          appToken: result.app.appToken,
          ...(tableId ? { tableId } : {}),
          ...(viewId ? { viewId } : {}),
        },
      });
      return result;
    } catch (error) {
      if (metadataRequestGuardRef.current.isLatest(requestId)) {
        throw error;
      }
      return undefined;
    } finally {
      if (metadataRequestGuardRef.current.isLatest(requestId)) {
        setLoadingMetadata(false);
      }
    }
  };

  const fieldOptions = metadata.fields.map(({ fieldId, name }) => ({
    label: name,
    value: fieldId,
  }));

  return (
    <ModalForm
      form={form}
      title={editing ? '修改采集配置' : '创建采集配置'}
      trigger={trigger}
      width={760}
      initialValues={getFeishuStoreCollectionInitialValues(record)}
      modalProps={{ destroyOnHidden: true }}
      onOpenChange={(open) => {
        if (!open) {
          metadataRequestGuardRef.current.invalidate();
          setMetadata({ fields: [], tables: [], views: [] });
          setLoadingMetadata(false);
          setMetadataLoaded(false);
          return;
        }
        if (record) {
          loadMetadata({
            connectionId: record.connectionId,
            sourceReference: record.sourceConfig.sourceUrl || record.sourceConfig.appToken,
            tableId: record.sourceConfig.tableId,
            viewId: record.sourceConfig.viewId,
          }).catch((error) => message.error(error.message));
        }
      }}
      submitter={{ submitButtonProps: { disabled: loadingMetadata || !metadataLoaded } }}
      onFinish={async (values) => {
        try {
          const collectionValues = buildFeishuStoreCollectionValues(values, metadata);
          await (editing ? updateCollection : createCollection)({
            data: { ...collectionValues, collectionType, id: record?.id, organizationId },
          });
          onSaved();
          return true;
        } catch (error) {
          message.error(error.message);
          return false;
        }
      }}
    >
      <ProFormText name="name" label="采集配置名称" rules={[{ required: true }]} />
      <ProFormSelect
        name="connectionId"
        label="飞书连接"
        options={connections.map(({ id, name }) => ({ label: name, value: id }))}
        fieldProps={{ onChange: resetSourceMetadata }}
        rules={[{ required: true }]}
      />
      <ProFormDateTimePicker
        name={['sourceConfig', 'startTime']}
        label="采集起始时间"
        rules={[{ required: true }]}
      />
      <ProFormText
        name="sourceReference"
        label="多维表格链接"
        rules={[{ required: true }]}
        fieldProps={{
          onChange: resetSourceMetadata,
          addonAfter: (
            <Button
              type="text"
              loading={loadingMetadata}
              onClick={() =>
                loadMetadata({ applySourceSelection: true }).catch((error) =>
                  message.error(error.message),
                )
              }
            >
              读取
            </Button>
          ),
        }}
      />
      <ProFormText name={['sourceConfig', 'appToken']} hidden />
      <ProFormSelect
        name={['sourceConfig', 'tableId']}
        label="数据表"
        options={metadata.tables.map(({ tableId, name }) => ({ label: name, value: tableId }))}
        fieldProps={{
          onChange: (tableId) => {
            form.setFieldsValue(getFeishuStoreCollectionResetValues({ viewId: undefined }));
            setMetadata((current) => ({ ...current, fields: [], views: [] }));
            setMetadataLoaded(false);
            loadMetadata({ tableId }).catch((error) => message.error(error.message));
          },
        }}
        rules={[{ required: true }]}
      />
      <ProFormSelect
        name={['sourceConfig', 'viewId']}
        label="视图"
        options={metadata.views.map(({ viewId, name }) => ({ label: name, value: viewId }))}
        rules={[{ required: true }]}
      />
      <ProFormSelect
        name={['sourceConfig', 'cursorFieldId']}
        label="最后更新时间字段"
        options={getFeishuCursorFieldOptions(metadata.fields)}
        rules={[{ required: true }]}
      />
      {STORE_FIELD_MAPPINGS.map(({ key, label, required }) => (
        <ProFormSelect
          key={key}
          name={['fieldMapping', key]}
          label={`${label}字段`}
          options={fieldOptions}
          rules={required ? [{ required: true }] : undefined}
          fieldProps={{ allowClear: !required, showSearch: true, optionFilterProp: 'label' }}
        />
      ))}
    </ModalForm>
  );
};

AgentFeishuStoreCollectionForm.propTypes = {
  collectionType: PropTypes.string.isRequired,
  connections: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string.isRequired }),
  ).isRequired,
  onSaved: PropTypes.func.isRequired,
  organizationId: PropTypes.string.isRequired,
  record: PropTypes.shape({
    connectionId: PropTypes.string.isRequired,
    fieldMapping: PropTypes.shape(
      Object.fromEntries(STORE_FIELD_MAPPINGS.map(({ key }) => [key, PropTypes.string])),
    ),
    id: PropTypes.string,
    name: PropTypes.string,
    sourceConfig: PropTypes.shape({
      sourceUrl: PropTypes.string,
      appToken: PropTypes.string.isRequired,
      cursorFieldId: PropTypes.string.isRequired,
      tableId: PropTypes.string.isRequired,
      viewId: PropTypes.string,
      startTime: PropTypes.number.isRequired,
    }),
  }),
  trigger: PropTypes.node.isRequired,
};

export default AgentFeishuStoreCollectionForm;
